import { ReportJSON, isDefined } from '@aloft/toolkit-shared';

import {
  COST_APPROACH_SITE_COMMENT,
  getCostApproachReplacementCostComment,
} from '../../../summaryV2/CostApproachSummary/consts.ts';
import { scalarToNumberToString } from '../../utils.ts';
import { createDirtyNode, createNodeWithText } from '../utils.ts';

export function create2055FormNode(doc: XMLDocument, reportJSON: ReportJSON): HTMLElement {
  const form = createDirtyNode(doc, 'FORM');

  // Assume the major form is always the first form
  form.setAttribute('NUM', '1');
  form.setAttribute('FORMCODE', '2055UAD'); // Is this 2055 or 2055 UAD? What's the difference
  form.setAttribute('SECCODE', '1');
  form.setAttribute('DESC', 'Exterior-Only');
  form.setAttribute('MAJOR', 'True');

  form.setAttribute('COMPSLOTS', '3');
  form.setAttribute('FIRSTCOMP', '1');

  const fields = createDirtyNode(doc, 'FIELDS');
  form.appendChild(fields);

  const allSubjectNodes = generateSubjectFieldNodes(doc, reportJSON.meta);
  fields.append(...allSubjectNodes);

  const allCostApproachNodes = generateCostApproachNodes(doc, reportJSON.subject.costApproach);
  fields.append(...allCostApproachNodes);

  const formPhotos = createDirtyNode(doc, 'FORMPHOTOS');
  form.appendChild(formPhotos);

  const attachments = createDirtyNode(doc, 'ATTACHMENTS');
  form.appendChild(attachments);

  return form;
}

function generateSubjectFieldNodes(doc: XMLDocument, meta?: ReportJSON['meta']): HTMLElement[] {
  const subjectNodes: HTMLElement[] = [];

  subjectNodes.push(createNodeWithText(doc, 'GSU_CM_OTHR1_TEXT', 'Add. Features'));
  if (isDefined(meta?.otherFeature2Label)) {
    subjectNodes.push(createNodeWithText(doc, 'GSU_CM_OTHR2_TEXT', meta?.otherFeature2Label));
  }

  return subjectNodes;
}

function generateCostApproachNodes(
  doc: XMLDocument,
  costApproach: ReportJSON['subject']['costApproach'],
): HTMLElement[] {
  const { remainingEcoLife, effectiveAge } = costApproach ?? {};

  const costApproachNodes: HTMLElement[] = [];

  costApproachNodes.push(createNodeWithText(doc, 'SITE_OPINION_1', COST_APPROACH_SITE_COMMENT));
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTCOMMENTS_1',
      getCostApproachReplacementCostComment(costApproach.source?.name ?? 'Craftsman'),
    ),
  );

  costApproachNodes.push(createNodeWithText(doc, 'CST_REPRODUCTION', ''));
  costApproachNodes.push(createNodeWithText(doc, 'CST_REPLACEMENT', 'X'));

  costApproachNodes.push(createNodeWithText(doc, 'CST_DATASRC', costApproach.source?.name ?? ''));

  costApproachNodes.push(
    createNodeWithText(doc, 'CST_SERVQUAL', costApproach?.dwellingCostQuality ?? ''),
  );

  costApproachNodes.push(
    createNodeWithText(doc, 'CST_DATADATE', costApproach.source?.effectiveDate.toString()),
  );

  costApproachNodes.push(
    createNodeWithText(doc, 'CSTESTSITEVAL', scalarToNumberToString(costApproach?.site)),
  );

  costApproachNodes.push(
    createNodeWithText(doc, 'CSTDWELLSF', scalarToNumberToString(costApproach.dwelling?.sqft)),
  );

  // If this is set as true ('X') then our sqft values for cost approachmwill not carry over, it will
  // be calculated based on the report's values. Even for a new report, the sqft will be blank, and
  // will not attempt to use the value we provide in the tdcx. Therefore we set it to false for now ('')
  costApproachNodes.push(createNodeWithText(doc, 'USECOSTAPPR_YES', ''));

  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTDWELLPPSF',
      scalarToNumberToString(costApproach.dwelling?.costPerSqft),
    ),
  );

  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTDWELLCOST',
      scalarToNumberToString(costApproach.dwelling?.totalCost),
    ),
  );

  // Mapped basement to the first other row, matching the cost approach table in Toolkit
  costApproachNodes.push(
    createNodeWithText(doc, 'CSTOTHER_DESC', costApproach?.basement ? 'Basement' : ''),
  );
  costApproachNodes.push(
    createNodeWithText(doc, 'CSTOTHERSF', scalarToNumberToString(costApproach.basement?.sqft)) ??
      '',
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTOTHERPPSF',
      scalarToNumberToString(costApproach.basement?.costPerSqft),
    ) ?? '',
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTOTHERCOST',
      scalarToNumberToString(costApproach.basement?.totalCost),
    ) ?? '',
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTEXTRAS',
      costApproach?.additionalStructures ? 'Add. Structures' : '',
    ),
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTEXTRASCOST',
      scalarToNumberToString(costApproach?.additionalStructures?.totalCost) ?? '',
    ),
  );

  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTGARCPTSF',
      scalarToNumberToString(costApproach.garageCarport?.sqft),
    ),
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTGARCPTPPSF',
      scalarToNumberToString(costApproach.garageCarport?.costPerSqft),
    ),
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTGARCPTCOST',
      scalarToNumberToString(costApproach.garageCarport?.totalCost),
    ),
  );

  costApproachNodes.push(createNodeWithText(doc, 'CSTCOSTNEW_TEXT', ''));
  costApproachNodes.push(
    createNodeWithText(doc, 'CSTTOTCOSTNEW', scalarToNumberToString(costApproach?.new?.cost)),
  );
  // Left blank as it's only used in the depreciation 'scrapbook'
  costApproachNodes.push(createNodeWithText(doc, 'CF_CA_AGE', ''));
  costApproachNodes.push(createNodeWithText(doc, 'CF_CA_LIFESPAN', ''));

  // Depreciation Percentage and Lump Sum
  costApproachNodes.push(createNodeWithText(doc, 'CSTPHYSDEPR', ''));
  costApproachNodes.push(createNodeWithText(doc, 'CSTPHYSDEPRLUMP', ''));
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTPHYSDEPR2',
      scalarToNumberToString(costApproach.depreciation?.physical),
    ),
  );
  costApproachNodes.push(createNodeWithText(doc, 'CSTFUNCDEPR', ''));
  costApproachNodes.push(createNodeWithText(doc, 'CSTFUNCDEPRLUMP', ''));
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTFUNCDEPR2',
      scalarToNumberToString(costApproach.depreciation?.functional),
    ),
  );
  costApproachNodes.push(createNodeWithText(doc, 'CSTEXTNDEPR', ''));
  costApproachNodes.push(createNodeWithText(doc, 'CSTEXTNDEPRLUMP', ''));
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTEXTNDEPR2',
      scalarToNumberToString(costApproach.depreciation?.external),
    ),
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTTOTALDEPR',
      scalarToNumberToString(costApproach.depreciation?.total),
    ),
  );

  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTDEPRVALUE',
      scalarToNumberToString(costApproach?.depreciated?.cost),
    ),
  );
  costApproachNodes.push(createNodeWithText(doc, 'CSTASISVALUE', ''));

  costApproachNodes.push(
    createNodeWithText(doc, 'CSTINDVALUE', scalarToNumberToString(costApproach?.indicatedValue)),
  );

  costApproachNodes.push(createNodeWithText(doc, 'ESTREMECONLIFE', `${remainingEcoLife ?? ''}`));
  costApproachNodes.push(createNodeWithText(doc, 'GDSEFFECTAGE', `${effectiveAge ?? ''}`));

  return costApproachNodes;
}
